// ============================================================================
// File: CollectionHealthPanel.tsx — Spec 160 T016
// Author: Peter Dobler | Created: 2026-05-31
// Feature: 160 - Dashboard Redesign (US1/US2 — Admin/Operator, not shown for Manager)
// Constitution: v1.15.0 | Principle XXXVII
// ============================================================================
import React from 'react';
import { DashboardPanel, Badge, Spinner, DataTable, ErrorState } from '@/components/ui';
import type { DataTableColumn, BadgeVariant } from '@/components/ui';
import { formatEastern, formatEasternTime } from '@/utils/dateFormatter';
import type { DashboardKpis } from '../../../../../shared/types/dashboard';

interface CollectionHealthPanelProps {
  data: DashboardKpis['collectionHealth'] | undefined;
  loading?: boolean;
}

type TierRow = NonNullable<DashboardKpis['collectionHealth']>['tiers'][number];

const statusVariant = (s: string | null): BadgeVariant => {
  if (!s) return 'default';
  if (s === 'Completed') return 'success';
  if (s === 'Failed') return 'error';
  if (s === 'Partial') return 'warning';
  return 'default';
};

const runTime = (iso: string | null | undefined): number => (iso ? new Date(iso).getTime() : 0);

export const CollectionHealthPanel: React.FC<CollectionHealthPanelProps> = ({
  data, loading = false,
}) => {
  // Default order: most-recent Last Run first (header click can re-sort).
  const tiers = [...(data?.tiers ?? [])].sort((a, b) => runTime(b.lastRunAt) - runTime(a.lastRunAt));

  const columns: DataTableColumn<TierRow>[] = [
    { key: 'tierName', header: 'Tier', cellClassName: 'font-semibold' },
    {
      key: 'lastStatus',
      header: 'Status',
      render: (t) => <Badge variant={statusVariant(t.lastStatus)} size="sm">{t.lastStatus ?? 'Unknown'}</Badge>,
    },
    {
      // Spec 177 US2: consecutive Failed/Partial runs; omit (—) when zero.
      key: 'consecutiveFailures',
      header: 'Failures',
      render: (t) =>
        t.consecutiveFailures > 0 ? (
          <Badge variant="error" size="sm">{t.consecutiveFailures} consecutive</Badge>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
    {
      key: 'lastRunAt',
      header: 'Last Run',
      align: 'right',
      sortValue: (t) => runTime(t.lastRunAt),
      render: (t) =>
        t.lastRunAt
          ? formatEastern(t.lastRunAt)
          : '—',
    },
  ];

  return (
    <DashboardPanel
      title="Collection Health"
      actions={
        data?.lastHeartbeat ? (
          <span className="text-xs text-[var(--color-text-secondary)]">
            HB: {formatEasternTime(data.lastHeartbeat)}
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <div className="flex min-h-[100px] items-center justify-center"><Spinner size="md" /></div>
      ) : !data ? (
        // Spec 298 FR-005/FR-006: a section that was not read is said so, never shown as zeros
        <ErrorState title="Could not read collection health" />
      ) : (
        <DataTable
          columns={columns}
          rows={tiers}
          rowKey={(t) => t.tierName}
          dense
          emptyMessage="No tier data — run the 5min tier"
        />
      )}
    </DashboardPanel>
  );
};

export default CollectionHealthPanel;
